import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, ArrowLeft, Upload, BarChart3, FileText, Play, Download,
  Loader2, CheckCircle, AlertCircle, Brain, TrendingUp, PieChart, Home, LogOut, FileDown, Trash2, Video, CreditCard, Info, HelpCircle, ClipboardList, Calculator, BookOpen, Save, Edit2, AlertTriangle, FlaskConical, MessageSquare
} from 'lucide-react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, ScatterChart, Scatter, Line, ComposedChart, ReferenceLine
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ['#4338ca', '#059669', '#0ea5e9', '#f59e0b', '#f43f5e', '#8b5cf6', '#10b981'];
const LIKERT_LABELS = { '1': 'Strongly Disagree', '2': 'Disagree', '3': 'Neutral', '4': 'Agree', '5': 'Strongly Agree' };

// Test explanation data
const TEST_EXPLANATIONS = {
  descriptive: {
    title: "Descriptive Statistics",
    purpose: "Summarize and describe the main features of your data using measures of central tendency and variability.",
    requirements: ["Numeric variables (continuous or discrete)", "Minimum 3 data points recommended"],
    assumptions: ["None - works with any numeric data"],
  },
  correlation: {
    title: "Correlation Analysis",
    purpose: "Measure the strength and direction of linear relationships between two or more variables.",
    requirements: ["Two or more numeric variables", "Paired observations"],
    assumptions: ["Linearity (Pearson)", "No significant outliers", "For Pearson: normally distributed variables"],
  },
  regression: {
    title: "Regression Analysis",
    purpose: "Predict a dependent variable based on one or more independent variables and understand their relationships.",
    requirements: ["One dependent variable (outcome)", "One or more independent variables (predictors)", "Numeric data"],
    assumptions: ["Linear relationship", "Independence of errors", "Homoscedasticity (constant variance)", "Normally distributed residuals"],
  },
  cronbach: {
    title: "Cronbach's Alpha",
    purpose: "Measure internal consistency reliability of a scale or questionnaire items.",
    requirements: ["Multiple items measuring the same construct", "Likert-scale or similar ordinal data", "Minimum 3 items"],
    assumptions: ["Items measure a single unidimensional construct", "Tau-equivalence (equal factor loadings)"],
  },
  ttest: {
    title: "Independent Samples T-Test",
    purpose: "Compare means between two independent groups to determine if there is a statistically significant difference.",
    requirements: ["One continuous dependent variable", "One categorical grouping variable with exactly 2 groups"],
    assumptions: ["Normal distribution in each group", "Equal variances (homogeneity)", "Independence of observations"],
  },
  ztest: {
    title: "Z-Test (One Sample)",
    purpose: "Compare a sample mean to a known population mean when you have a large sample (n≥30).",
    requirements: ["Sample size of at least 30", "Known or estimated population parameters", "One numeric variable"],
    assumptions: ["Large sample size (n≥30)", "Random sampling", "Data approximately normally distributed"],
  },
  anova: {
    title: "One-Way ANOVA",
    purpose: "Compare means across three or more groups to determine if at least one group differs significantly.",
    requirements: ["One continuous dependent variable", "One categorical grouping variable with 3+ groups"],
    assumptions: ["Normal distribution in each group", "Homogeneity of variances", "Independence of observations"],
  },
  chi_square: {
    title: "Chi-Square Test",
    purpose: "Test for association between two categorical variables.",
    requirements: ["Two categorical variables", "Expected cell frequencies ≥ 5"],
    assumptions: ["Independence of observations", "Adequate expected frequencies", "Random sampling"],
  },
  mann_whitney: {
    title: "Mann-Whitney U Test",
    purpose: "Non-parametric alternative to t-test for comparing two independent groups when assumptions are violated.",
    requirements: ["One ordinal or continuous variable", "Two independent groups"],
    assumptions: ["Independent observations", "Ordinal or continuous dependent variable"],
  },
  kruskal_wallis: {
    title: "Kruskal-Wallis H Test",
    purpose: "Non-parametric alternative to ANOVA for comparing three or more groups.",
    requirements: ["One ordinal or continuous variable", "Three or more independent groups"],
    assumptions: ["Independent observations", "Similar distribution shapes across groups"],
  },
  wilcoxon: {
    title: "Wilcoxon Signed-Rank Test",
    purpose: "Non-parametric alternative to paired t-test for comparing two related measurements.",
    requirements: ["Two related measurements (pre/post)", "Ordinal or continuous data"],
    assumptions: ["Paired observations", "Symmetric distribution of differences"],
  },
  thematic: {
    title: "Thematic Analysis",
    purpose: "Identify, analyze, and report patterns (themes) within qualitative data.",
    requirements: ["Text data (interviews, transcripts, open-ended responses)"],
    assumptions: ["Sufficient text for theme identification", "Researcher familiarity with data"],
  },
  sentiment: {
    title: "Sentiment Analysis",
    purpose: "Determine the emotional tone and attitudes expressed in text data.",
    requirements: ["Text data with opinions or emotional content"],
    assumptions: ["Text contains sentiment-bearing words", "Language is analyzable by the system"],
  },
  wordcloud: {
    title: "Word Cloud",
    purpose: "Visualize the most frequent words in your text data for quick pattern identification.",
    requirements: ["Text data"],
    assumptions: ["None - descriptive visualization"],
  },
  keyword_context: {
    title: "Keyword Context Search",
    purpose: "Find and display sentences containing specific keywords to understand usage context.",
    requirements: ["Text data", "Keywords to search"],
    assumptions: ["Keywords exist in the text"],
  },
  critical_analysis: {
    title: "Critical Analysis (AI-Powered)",
    purpose: "Generate a scholarly critical discussion of interview data, analyzing participant perspectives, agreements, and disagreements.",
    requirements: ["Qualitative interview data", "Multiple participant responses preferred"],
    assumptions: ["Text contains identifiable participant perspectives"],
  },
};

// Test Explanation Component
const TestExplanation = ({ analysisType }) => {
  const [expanded, setExpanded] = useState(false);
  const explanation = TEST_EXPLANATIONS[analysisType];
  
  if (!explanation) return null;
  
  return (
    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-blue-700 font-medium text-sm w-full"
      >
        <HelpCircle className="w-4 h-4" />
        <span>About {explanation.title}</span>
        <span className="ml-auto text-xs">{expanded ? '▼' : '▶'}</span>
      </button>
      
      {expanded && (
        <div className="mt-3 space-y-2 text-sm">
          <div>
            <span className="font-medium text-blue-800">Purpose: </span>
            <span className="text-blue-700">{explanation.purpose}</span>
          </div>
          <div>
            <span className="font-medium text-blue-800">Requirements:</span>
            <ul className="list-disc list-inside text-blue-700 ml-2">
              {explanation.requirements.map((req, i) => (
                <li key={i}>{req}</li>
              ))}
            </ul>
          </div>
          <div>
            <span className="font-medium text-blue-800">Assumptions:</span>
            <ul className="list-disc list-inside text-blue-700 ml-2">
              {explanation.assumptions.map((assumption, i) => (
                <li key={i}>{assumption}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [project, setProject] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [supervisorAnalysisComments, setSupervisorAnalysisComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Analysis form state
  const [analysisType, setAnalysisType] = useState('descriptive');
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [dependentVar, setDependentVar] = useState('');
  const [groupVar, setGroupVar] = useState('');
  const [correlationMethod, setCorrelationMethod] = useState('pearson');
  const [currentResults, setCurrentResults] = useState(null);
  const [interpretation, setInterpretation] = useState('');
  
  // Z-test state
  const [populationMean, setPopulationMean] = useState('');
  const [populationStd, setPopulationStd] = useState('');
  
  // Critical analysis state
  const [criticalAnalysis, setCriticalAnalysis] = useState(null);
  const [runningCritical, setRunningCritical] = useState(false);
  
  // Keyword context search state
  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordResults, setKeywordResults] = useState(null);
  const [searchingKeywords, setSearchingKeywords] = useState(false);
  
  // Post-hoc test state
  const [postHocResults, setPostHocResults] = useState(null);
  const [runningPostHoc, setRunningPostHoc] = useState(false);

  // Editable project details state
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedAim, setEditedAim] = useState('');
  const [editedObjectives, setEditedObjectives] = useState('');
  const [savingDetails, setSavingDetails] = useState(false);
  const [generatingAim, setGeneratingAim] = useState(false);
  const [generatingObjectives, setGeneratingObjectives] = useState(false);
  
  // Overwrite confirmation state
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [pendingAnalysis, setPendingAnalysis] = useState(null);
  
  // Selected saved analysis for viewing
  const [selectedSavedAnalysis, setSelectedSavedAnalysis] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(res.data);
      
      // If subscription is inactive, redirect to dashboard
      if (res.data.status !== 'active') {
        toast.error('Your subscription is inactive. Please subscribe to access projects.');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Failed to fetch subscription status');
    }
  }, [token, navigate]);

  const [youtubeChannelUrl, setYoutubeChannelUrl] = useState('');
  
  const fetchYoutubeChannel = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/youtube-channel`);
      setYoutubeChannelUrl(res.data.url || '');
    } catch (err) {
      console.log('No YouTube channel configured');
    }
  }, []);

  const fetchProject = useCallback(async () => {
    try {
      const [projRes, dataRes, analysisRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/datasets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/analyses`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProject(projRes.data);
      setDatasets(dataRes.data);
      setAnalyses(analysisRes.data);
      // Load supervisor comments on analyses (section_id = 'analysis-results') - silent on error
      try {
        const cRes = await axios.get(`${API}/projects/${projectId}/comments`, { headers: { Authorization: `Bearer ${token}` } });
        setSupervisorAnalysisComments((cRes.data || []).filter((c) => c.section_id === 'analysis-results'));
      } catch (_) { /* ignore */ }
    } catch (err) {
      toast.error('Failed to load project');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId, token, navigate]);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchYoutubeChannel();
  }, [fetchSubscriptionStatus, fetchYoutubeChannel]);

  useEffect(() => {
    if (subscriptionStatus?.status === 'active') {
      fetchProject();
    }
  }, [fetchProject, subscriptionStatus]);

  // Initialize edit fields when project loads
  useEffect(() => {
    if (project) {
      setEditedTitle(project.title || '');
      setEditedAim(project.research_aim || '');
      setEditedObjectives(project.research_objectives || '');
    }
  }, [project]);

  const handleSaveDetails = async () => {
    setSavingDetails(true);
    try {
      const res = await axios.patch(
        `${API}/projects/${projectId}/details`,
        { title: editedTitle, research_aim: editedAim, research_objectives: editedObjectives },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(res.data);
      setIsEditingDetails(false);
      toast.success('Research details saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save details');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleGenerateAim = async () => {
    if (!project?.title?.trim()) {
      toast.error('Project needs a title first');
      return;
    }
    setGeneratingAim(true);
    try {
      const res = await axios.post(`${API}/ai/generate-aim`, 
        { title: project.title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update project with generated aim
      const updateRes = await axios.patch(
        `${API}/projects/${projectId}/details`,
        { research_aim: res.data.aim },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updateRes.data);
      setEditedAim(res.data.aim);
      toast.success('Aim generated & saved!');
    } catch (err) {
      toast.error('Failed to generate aim');
    } finally {
      setGeneratingAim(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!project?.title?.trim() || !project?.research_aim?.trim()) {
      toast.error('Project needs title and aim first');
      return;
    }
    setGeneratingObjectives(true);
    try {
      const res = await axios.post(`${API}/ai/generate-objectives`, 
        { 
          title: project.title, 
          aim: project.research_aim,
          research_type: project.research_type || 'quantitative'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update project with generated objectives
      const updateRes = await axios.patch(
        `${API}/projects/${projectId}/details`,
        { research_objectives: res.data.objectives },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProject(updateRes.data);
      setEditedObjectives(res.data.objectives);
      toast.success('Objectives generated & saved!');
    } catch (err) {
      toast.error('Failed to generate objectives');
    } finally {
      setGeneratingObjectives(false);
    }
  };

  const checkExistingAnalysis = (analysisType) => {
    return analyses.find(a => a.analysis_type === analysisType);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      await axios.post(
        `${API}/projects/${projectId}/upload`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Data uploaded successfully!');
      // Clear selected variables so user selects from new dataset columns
      setSelectedVariables([]);
      await fetchProject();
      setActiveTab('analysis');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDataset = async (datasetId, filename) => {
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/datasets/${datasetId}/download`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started!');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleDeleteDataset = async (datasetId, filename) => {
    if (!window.confirm(`Delete "${filename}"? Any analyses using this dataset will also be deleted.`)) return;
    
    try {
      await axios.delete(
        `${API}/projects/${projectId}/datasets/${datasetId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Dataset deleted');
      // Clear selected dataset if it was the deleted one
      if (selectedDatasetId === datasetId) {
        setSelectedDatasetId('');
      }
      fetchProject();
    } catch (err) {
      toast.error('Failed to delete dataset');
    }
  };

  const handleDownloadSample = async (type) => {
    const endpoints = {
      quantitative: '/samples/quantitative',
      qualitative: '/samples/qualitative',
      likert: '/samples/likert',
      ttest: '/samples/ttest',
      anova: '/samples/anova',
      chisquare: '/samples/chisquare',
      cronbach: '/samples/cronbach',
      mann_whitney: '/samples/mann_whitney',
      kruskal_wallis: '/samples/kruskal_wallis',
      wilcoxon: '/samples/wilcoxon',
      ztest: '/samples/ztest'
    };
    const extensions = {
      qualitative: 'txt'
    };
    try {
      const response = await axios.get(`${API}${endpoints[type]}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sample_${type}_data.${extensions[type] || 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sample data downloaded!');
    } catch (err) {
      toast.error('Failed to download sample');
    }
  };

  const [selectedDatasetId, setSelectedDatasetId] = useState('');

  const executeAnalysis = async (requestBody) => {
    setAnalyzing(true);
    setInterpretation('');
    setPostHocResults(null);
    setCriticalAnalysis(null);
    try {
      const res = await axios.post(`${API}/analysis/run`, requestBody, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentResults(res.data);
      // Remove old analysis of same type and add new one
      setAnalyses([res.data, ...analyses.filter(a => a.analysis_type !== requestBody.analysis_type)]);
      toast.success('Analysis complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRunAnalysis = async () => {
    // Handle critical analysis separately
    if (analysisType === 'critical_analysis') {
      handleRunCriticalAnalysis();
      return;
    }
    
    if (datasets.length === 0) {
      toast.error('Please upload data first');
      return;
    }

    const requestBody = {
      project_id: projectId,
      analysis_type: analysisType,
      dataset_id: selectedDatasetId || null,
      variables: selectedVariables.length > 0 ? selectedVariables : null,
      options: {}
    };

    if (analysisType === 'regression' && dependentVar) {
      requestBody.options.dependent = dependentVar;
      requestBody.options.independents = selectedVariables.filter(v => v !== dependentVar);
    }

    if (['ttest', 'anova', 'mann_whitney', 'kruskal_wallis'].includes(analysisType) && groupVar) {
      requestBody.options.variable = selectedVariables[0];
      requestBody.options.group_var = groupVar;
    }

    if (analysisType === 'correlation') {
      requestBody.options.method = correlationMethod;
    }
    
    if (analysisType === 'ztest') {
      if (!populationMean) {
        toast.error('Please enter the population mean');
        return;
      }
      requestBody.options.population_mean = parseFloat(populationMean);
      if (populationStd) {
        requestBody.options.population_std = parseFloat(populationStd);
      }
    }

    // Check if analysis of this type already exists
    const existingAnalysis = checkExistingAnalysis(analysisType);
    if (existingAnalysis) {
      setPendingAnalysis(requestBody);
      setShowOverwriteDialog(true);
      return;
    }

    await executeAnalysis(requestBody);
  };

  const handleConfirmOverwrite = async () => {
    setShowOverwriteDialog(false);
    if (pendingAnalysis) {
      await executeAnalysis(pendingAnalysis);
      setPendingAnalysis(null);
    }
  };
  
  const handleRunCriticalAnalysis = async () => {
    setRunningCritical(true);
    setCriticalAnalysis(null);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/critical-analysis`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCriticalAnalysis(res.data);
      toast.success('Critical analysis generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Critical analysis failed');
    } finally {
      setRunningCritical(false);
    }
  };

  const handleKeywordSearch = async () => {
    if (!keywordSearch.trim()) {
      toast.error('Enter at least one keyword');
      return;
    }

    setSearchingKeywords(true);
    setKeywordResults(null);
    try {
      const formData = new FormData();
      formData.append('keywords', keywordSearch);
      if (selectedDatasetId) {
        formData.append('dataset_id', selectedDatasetId);
      }

      const res = await axios.post(
        `${API}/projects/${projectId}/keyword-context`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKeywordResults(res.data);
      toast.success('Keyword search complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Keyword search failed');
    } finally {
      setSearchingKeywords(false);
    }
  };

  const handleRunPostHoc = async () => {
    if (!currentResults || currentResults.analysis_type !== 'anova') {
      toast.error('Run ANOVA first');
      return;
    }

    setRunningPostHoc(true);
    setPostHocResults(null);
    try {
      const res = await axios.post(`${API}/analysis/run`, {
        project_id: projectId,
        analysis_type: 'posthoc_tukey',
        dataset_id: selectedDatasetId,
        variables: selectedVariables,
        options: {
          variable: currentResults.results.variable,
          group_var: currentResults.results.group_var
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPostHocResults(res.data.results);
      toast.success('Post-hoc test complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Post-hoc test failed');
    } finally {
      setRunningPostHoc(false);
    }
  };

  const handleGetInterpretation = async () => {
    if (!currentResults) {
      toast.error('Run an analysis first');
      return;
    }

    setInterpreting(true);
    try {
      const formData = new FormData();
      formData.append('analysis_type', currentResults.analysis_type);
      formData.append('results', JSON.stringify(currentResults.results));
      formData.append('research_context', project.research_aim);

      const res = await axios.post(`${API}/analysis/interpret`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterpretation(res.data.interpretation);
      
      // Save interpretation to database
      if (currentResults.analysis_id) {
        const saveFormData = new FormData();
        saveFormData.append('interpretation', res.data.interpretation);
        await axios.patch(`${API}/analyses/${currentResults.analysis_id}/interpretation`, saveFormData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Interpretation generated & saved!');
    } catch (err) {
      toast.error('Failed to generate interpretation');
    } finally {
      setInterpreting(false);
    }
  };

  const toggleVariable = (varName) => {
    setSelectedVariables(prev => 
      prev.includes(varName) 
        ? prev.filter(v => v !== varName)
        : [...prev, varName]
    );
  };

  const getDatasetColumns = () => {
    // If a specific dataset is selected, use its columns
    if (selectedDatasetId) {
      const selectedDs = datasets.find(d => d.id === selectedDatasetId);
      if (selectedDs?.columns) return selectedDs.columns;
      if (selectedDs?.validation?.column_names) return selectedDs.validation.column_names;
    }
    // Otherwise use first quantitative dataset
    const quantDataset = datasets.find(d => d.data_type === 'quantitative');
    if (quantDataset?.columns) return quantDataset.columns;
    if (quantDataset?.validation?.column_names) return quantDataset.validation.column_names;
    return [];
  };

  // Reset selected variables when dataset changes
  const handleDatasetChange = (value) => {
    setSelectedDatasetId(value === "auto" ? "" : value);
    setSelectedVariables([]); // Clear selected variables when dataset changes
  };

  // Get columns - this will update when datasets or selectedDatasetId changes
  const columns = getDatasetColumns();

  const renderDescriptiveResults = (results) => {
    const basicStats = results.basic_stats || results;
    const likertDist = results.likert_distributions || {};
    const groupDist = results.group_distributions || {};
    const freqTables = results.frequency_tables || {};

    // Stat definitions for explanations
    const statDefinitions = {
      count: "Total number of valid observations",
      sum: "Sum of all values",
      mean: "Average value (sum ÷ count)",
      median: "Middle value when data is sorted",
      mode: "Most frequently occurring value",
      std: "Measures spread around the mean",
      variance: "Square of standard deviation",
      standard_error: "Precision of the sample mean estimate",
      min: "Smallest value in the data",
      max: "Largest value in the data",
      range: "Difference between max and min",
      skewness: "Measure of asymmetry (0 = symmetric)",
      kurtosis: "Measure of tail heaviness (0 = normal)",
      q1: "25th percentile (lower quartile)",
      q3: "75th percentile (upper quartile)",
      iqr: "Interquartile range (Q3 - Q1)",
      geometric_mean: "Nth root of product of values (for rates)",
      harmonic_mean: "Reciprocal of arithmetic mean of reciprocals",
      aad: "Average Absolute Deviation from mean",
      mad: "Median Absolute Deviation from median"
    };

    return (
      <div className="space-y-8">
        {/* Central Tendency */}
        <div>
          <h4 className="font-serif font-semibold text-lg mb-2">Central Tendency</h4>
          <p className="text-sm text-slate-500 mb-4">Measures that describe the center of the data distribution</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left">Variable</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.count}>N</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.sum}>Sum</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.mean}>Mean</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.median}>Median</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.mode}>Mode</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.geometric_mean}>Geo Mean</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.harmonic_mean}>Harm Mean</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(basicStats).map(([varName, stats]) => (
                  <tr key={varName} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{varName}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.count}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.sum?.toFixed(2) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.mean?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.median?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.mode?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.geometric_mean?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.harmonic_mean?.toFixed(4) ?? 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dispersion / Variability */}
        <div>
          <h4 className="font-serif font-semibold text-lg mb-2">Dispersion & Variability</h4>
          <p className="text-sm text-slate-500 mb-4">Measures that describe the spread of the data</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left">Variable</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.std}>Std Dev</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.variance}>Variance</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.standard_error}>Std Error</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.min}>Min</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.max}>Max</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.range}>Range</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.iqr}>IQR</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(basicStats).map(([varName, stats]) => (
                  <tr key={varName} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{varName}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.std?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.variance?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.standard_error?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.min?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.max?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.range?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.iqr?.toFixed(4) ?? 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Distribution Shape & Quartiles */}
        <div>
          <h4 className="font-serif font-semibold text-lg mb-2">Distribution Shape & Quartiles</h4>
          <p className="text-sm text-slate-500 mb-4">Measures describing data distribution characteristics</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left">Variable</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.skewness}>Skewness</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.kurtosis}>Kurtosis</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.q1}>Q1 (25%)</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.q3}>Q3 (75%)</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.aad}>AAD</th>
                  <th className="px-4 py-2 text-right" title={statDefinitions.mad}>MAD</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(basicStats).map(([varName, stats]) => (
                  <tr key={varName} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{varName}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={stats.skewness > 0.5 ? 'text-amber-600' : stats.skewness < -0.5 ? 'text-blue-600' : ''}>
                        {stats.skewness?.toFixed(4) ?? 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      <span className={stats.kurtosis > 1 ? 'text-amber-600' : stats.kurtosis < -1 ? 'text-blue-600' : ''}>
                        {stats.kurtosis?.toFixed(4) ?? 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">{stats.q1?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.q3?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.aad?.toFixed(4) ?? 'N/A'}</td>
                    <td className="px-4 py-2 text-right font-mono">{stats.mad?.toFixed(4) ?? 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stat Explanations */}
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Understanding These Statistics
          </h4>
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><strong>Mean:</strong> {statDefinitions.mean}</div>
            <div><strong>Median:</strong> {statDefinitions.median}</div>
            <div><strong>Mode:</strong> {statDefinitions.mode}</div>
            <div><strong>Std Dev:</strong> {statDefinitions.std}</div>
            <div><strong>Variance:</strong> {statDefinitions.variance}</div>
            <div><strong>Std Error:</strong> {statDefinitions.standard_error}</div>
            <div><strong>Skewness:</strong> {statDefinitions.skewness}</div>
            <div><strong>Kurtosis:</strong> {statDefinitions.kurtosis}</div>
            <div><strong>IQR:</strong> {statDefinitions.iqr}</div>
            <div><strong>AAD:</strong> {statDefinitions.aad}</div>
            <div><strong>MAD:</strong> {statDefinitions.mad}</div>
            <div><strong>Geometric Mean:</strong> {statDefinitions.geometric_mean}</div>
          </div>
        </div>

        {/* Mean Comparison Bar Chart */}
        {Object.keys(basicStats).length > 0 && (
          <div>
            <h4 className="font-serif font-semibold text-lg mb-4">Mean Comparison</h4>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={Object.entries(basicStats).map(([name, stats]) => ({ name, mean: stats.mean || 0 }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="mean" fill="#4338ca" name="Mean" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Group Distributions */}
        {Object.keys(groupDist).length > 0 && (
          <div>
            <h4 className="font-serif font-semibold text-lg mb-4">Group Distributions</h4>
            <div className="grid md:grid-cols-2 gap-6">
              {Object.entries(groupDist).map(([varName, groups]) => (
                <div key={varName} className="card-dashboard">
                  <h5 className="font-medium mb-3">{varName}</h5>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={groups} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} unit="%" />
                        <YAxis dataKey="group" type="category" width={80} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                        <Bar dataKey="percentage" fill="#059669" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    {groups.map(g => `${g.group}: ${g.count} (${g.percentage}%)`).join(' | ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Likert Scale Distributions */}
        {Object.keys(likertDist).length > 0 && (
          <div>
            <h4 className="font-serif font-semibold text-lg mb-4">Likert Scale Response Distributions</h4>
            <div className="space-y-6">
              {Object.entries(likertDist).map(([varName, dist]) => (
                <div key={varName} className="card-dashboard">
                  <h5 className="font-medium mb-3">{varName}</h5>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={Object.entries(dist).map(([val, data]) => ({
                        response: LIKERT_LABELS[val] || `Response ${val}`,
                        value: parseInt(val),
                        count: data.count,
                        percentage: data.percentage
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="response" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={60} />
                        <YAxis domain={[0, 100]} unit="%" />
                        <Tooltip formatter={(value, name) => [name === 'percentage' ? `${value}%` : value, name === 'percentage' ? 'Percentage' : 'Count']} />
                        <Bar dataKey="percentage" fill="#8b5cf6" name="percentage" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-2 grid grid-cols-5 gap-2 text-xs">
                    {Object.entries(dist).map(([val, data]) => (
                      <div key={val} className="text-center p-2 bg-slate-50 rounded">
                        <div className="font-medium">{val}</div>
                        <div className="text-slate-500">{data.count} ({data.percentage}%)</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCorrelationResults = (results) => {
    return (
      <div className="space-y-6">
        {/* Method Explanation */}
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
          <h5 className="font-semibold text-blue-900 mb-2">{results.method} Correlation</h5>
          <p className="text-sm text-blue-800">{results.method_description}</p>
        </div>

        {/* Correlation Matrix */}
        <div>
          <h4 className="font-serif font-semibold text-lg mb-4">Correlation Matrix</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left">Variable</th>
                  {Object.keys(results.correlation_matrix).map(v => (
                    <th key={v} className="px-4 py-2 text-right">{v}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(results.correlation_matrix).map(([var1, values]) => (
                  <tr key={var1} className="border-b">
                    <td className="px-4 py-2 font-medium">{var1}</td>
                    {Object.entries(values).map(([var2, val]) => {
                      const pVal = results.p_values?.[var1]?.[var2];
                      const isSignificant = pVal !== null && pVal < 0.05;
                      return (
                        <td key={var2} className={`px-4 py-2 text-right font-mono ${
                          var1 === var2 ? 'bg-slate-50' :
                          Math.abs(val) >= 0.7 ? 'text-indigo-700 font-bold' : 
                          Math.abs(val) >= 0.4 ? 'text-indigo-600' : ''
                        }`}>
                          {val?.toFixed(3) ?? '-'}
                          {isSignificant && var1 !== var2 && <span className="text-green-600 ml-1">*</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-500 mt-2">* indicates statistical significance (p {'<'} 0.05)</p>
          </div>
        </div>

        {/* Strength Guide */}
        <div className="card-dashboard">
          <h5 className="font-semibold mb-3">Correlation Strength Guide</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {Object.entries(results.correlation_strength_guide || {}).map(([range, label]) => (
              <div key={range} className="p-2 bg-slate-50 rounded text-center">
                <div className="font-mono text-indigo-700">{range}</div>
                <div className="text-slate-600 text-xs">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Auto Interpretations */}
        {results.interpretations && Object.keys(results.interpretations).length > 0 && (
          <div>
            <h5 className="font-semibold mb-3">Key Findings</h5>
            <div className="space-y-2">
              {Object.entries(results.interpretations).slice(0, 6).map(([key, text]) => (
                <div key={key} className="p-3 bg-slate-50 rounded-lg text-sm text-slate-700">
                  {text}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!currentResults) return null;
    const { analysis_type, results } = currentResults;

    switch (analysis_type) {
      case 'descriptive':
        return renderDescriptiveResults(results);

      case 'correlation':
        return renderCorrelationResults(results);

      case 'regression':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Regression Analysis</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">R²</p>
                <p className="text-2xl font-mono font-bold text-indigo-700">{results.r_squared?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Adjusted R²</p>
                <p className="text-2xl font-mono font-bold">{results.adjusted_r_squared?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Intercept</p>
                <p className="text-2xl font-mono font-bold">{results.intercept?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Sample Size</p>
                <p className="text-2xl font-mono font-bold">{results.sample_size}</p>
              </div>
            </div>
            
            {/* Scatter Plot with Regression Line */}
            {results.scatter_data && results.scatter_data.length > 0 && (
              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <h5 className="font-semibold mb-3">Scatter Plot with Regression Line</h5>
                <p className="text-xs text-slate-500 mb-2">
                  X-axis: {results.independent_variables?.[0] || 'Independent Variable'} | 
                  Y-axis: {results.dependent_variable || 'Dependent Variable'}
                </p>
                <ResponsiveContainer width="100%" height={320}>
                  <ComposedChart data={results.scatter_data} margin={{ top: 20, right: 30, bottom: 30, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="x" 
                      type="number" 
                      domain={['auto', 'auto']}
                      tickFormatter={(value) => typeof value === 'number' ? value.toFixed(1) : value}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis 
                      type="number" 
                      domain={['auto', 'auto']}
                      tickFormatter={(value) => typeof value === 'number' ? value.toFixed(1) : value}
                      tick={{ fontSize: 11 }}
                      width={50}
                    />
                    <Tooltip 
                      formatter={(value, name) => [
                        typeof value === 'number' ? value.toFixed(3) : value, 
                        name === 'y' ? 'Actual' : 'Predicted'
                      ]}
                      labelFormatter={(x) => `X: ${typeof x === 'number' ? x.toFixed(3) : x}`}
                    />
                    <Legend />
                    <Scatter name="Data Points" dataKey="y" fill="#4338ca" />
                    <Line 
                      name="Regression Line" 
                      type="linear" 
                      dataKey="predicted" 
                      stroke="#ef4444" 
                      strokeWidth={2} 
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="text-xs text-slate-500 text-center mt-2">
                  Blue dots = actual data points | Red line = predicted values (regression line)
                </p>
                {/* Regression equation */}
                {results.coefficients && results.intercept !== undefined && (
                  <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="regression-equation">
                    <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Regression Equation</p>
                    <p className="font-mono text-sm text-slate-900 break-words">
                      {(() => {
                        const dv = results.dependent_variable || 'Y';
                        const intercept = Number(results.intercept).toFixed(4);
                        const terms = Object.entries(results.coefficients || {}).map(([v, c]) => {
                          const num = Number(c);
                          const sign = num >= 0 ? ' + ' : ' − ';
                          return `${sign}${Math.abs(num).toFixed(4)} × (${v})`;
                        }).join('');
                        return `${dv} = ${intercept}${terms}`;
                      })()}
                    </p>
                    <p className="text-xs text-slate-500 mt-1.5">
                      Substitute any value of the predictor(s) into this equation to obtain the predicted value of <strong>{results.dependent_variable || 'the dependent variable'}</strong>.
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800">
              <strong>Interpretation:</strong> The model explains {(results.r_squared * 100).toFixed(1)}% of the variance in the dependent variable. 
              {results.r_squared >= 0.7 ? ' This indicates a strong model fit.' : 
               results.r_squared >= 0.4 ? ' This indicates a moderate model fit.' : 
               ' This indicates a weak model fit - consider adding more predictors.'}
            </div>
            <h5 className="font-semibold mt-4">Coefficients</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Variable</th>
                    <th className="px-4 py-2 text-right">Coefficient</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(results.coefficients || {}).map(([varName, coef]) => (
                    <tr key={varName} className="border-b">
                      <td className="px-4 py-2 font-medium">{varName}</td>
                      <td className="px-4 py-2 text-right font-mono">{coef?.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'ztest':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">One-Sample Z-Test</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Z-Statistic</p>
                <p className="text-2xl font-mono font-bold text-indigo-700">{results.z_statistic?.toFixed(4)}</p>
              </div>
              <div className={`card-dashboard text-center ${results.significant ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="label-ui mb-1">P-Value</p>
                <p className={`text-2xl font-mono font-bold ${results.significant ? 'text-green-700' : 'text-red-700'}`}>
                  {results.p_value?.toFixed(4)}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Sample Mean</p>
                <p className="text-2xl font-mono font-bold">{results.sample_mean?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Population Mean</p>
                <p className="text-2xl font-mono font-bold">{results.population_mean}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Sample Size (n)</p>
                <p className="text-xl font-mono font-bold">{results.sample_size}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Standard Error</p>
                <p className="text-xl font-mono font-bold">{results.standard_error?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">95% CI</p>
                <p className="text-lg font-mono font-bold">
                  [{results.confidence_interval_95?.lower?.toFixed(2)}, {results.confidence_interval_95?.upper?.toFixed(2)}]
                </p>
              </div>
            </div>
            <div className={`p-4 rounded-xl text-sm ${results.significant ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
              <strong>Conclusion:</strong> The sample mean ({results.sample_mean?.toFixed(2)}) is 
              {results.significant 
                ? ` significantly different from the population mean (${results.population_mean}) at α = 0.05.`
                : ` not significantly different from the population mean (${results.population_mean}) at α = 0.05.`}
            </div>
          </div>
        );

      case 'ttest':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Independent Samples T-Test</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">T-Statistic</p>
                <p className="text-2xl font-mono font-bold">{results.t_statistic?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className={`text-2xl font-mono font-bold ${results.p_value < 0.05 ? 'text-green-600' : 'text-slate-600'}`}>
                  {results.p_value?.toFixed(4)}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className="text-2xl">
                  {results.significant ? 
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto" /> : 
                    <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto" />
                  }
                </p>
              </div>
            </div>
            <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800">
              <strong>Interpretation:</strong> {results.significant 
                ? `There is a statistically significant difference between ${results.group1_name} (M=${results.group1_mean?.toFixed(2)}) and ${results.group2_name} (M=${results.group2_mean?.toFixed(2)}), t = ${results.t_statistic?.toFixed(3)}, p = ${results.p_value?.toFixed(4)}.`
                : `There is no statistically significant difference between the groups (p = ${results.p_value?.toFixed(4)} > 0.05).`}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">Group: {results.group1_name}</h5>
                <p>Mean: <span className="font-mono">{results.group1_mean?.toFixed(2)}</span></p>
                <p>Std Dev: <span className="font-mono">{results.group1_std?.toFixed(2)}</span></p>
                <p>N: <span className="font-mono">{results.group1_n}</span></p>
              </div>
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">Group: {results.group2_name}</h5>
                <p>Mean: <span className="font-mono">{results.group2_mean?.toFixed(2)}</span></p>
                <p>Std Dev: <span className="font-mono">{results.group2_std?.toFixed(2)}</span></p>
                <p>N: <span className="font-mono">{results.group2_n}</span></p>
              </div>
            </div>
          </div>
        );

      case 'anova':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">One-Way ANOVA</h4>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">F-Statistic</p>
                <p className="text-2xl font-mono font-bold">{results.f_statistic?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className={`text-2xl font-mono font-bold ${results.p_value < 0.05 ? 'text-green-600' : 'text-slate-600'}`}>
                  {results.p_value?.toFixed(4)}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className="text-2xl">
                  {results.significant ? 
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto" /> : 
                    <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto" />
                  }
                </p>
              </div>
            </div>
            <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800">
              <strong>Interpretation:</strong> {results.significant 
                ? `There is a statistically significant difference between at least two groups, F = ${results.f_statistic?.toFixed(3)}, p = ${results.p_value?.toFixed(4)}. Post-hoc tests are recommended to identify which groups differ.`
                : `There is no statistically significant difference between the groups (p = ${results.p_value?.toFixed(4)} > 0.05).`}
            </div>
            <h5 className="font-semibold">Group Statistics</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-2 text-left">Group</th>
                    <th className="px-4 py-2 text-right">Mean</th>
                    <th className="px-4 py-2 text-right">Std Dev</th>
                    <th className="px-4 py-2 text-right">N</th>
                  </tr>
                </thead>
                <tbody>
                  {results.group_statistics?.map((group, i) => (
                    <tr key={i} className="border-b">
                      <td className="px-4 py-2 font-medium">{group.group}</td>
                      <td className="px-4 py-2 text-right font-mono">{group.mean?.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-mono">{group.std?.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-mono">{group.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Bar chart for group means */}
            <div className="h-48 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={results.group_statistics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="group" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="mean" fill="#4338ca" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            {/* Post-Hoc Test Section */}
            {results.significant && results.group_statistics?.length >= 3 && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <h5 className="font-semibold text-amber-800 mb-2">Post-Hoc Analysis Recommended</h5>
                <p className="text-sm text-amber-700 mb-3">
                  ANOVA shows a significant difference. Run Tukey HSD post-hoc test to identify which specific groups differ.
                </p>
                <Button
                  onClick={handleRunPostHoc}
                  disabled={runningPostHoc}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="run-posthoc-btn"
                >
                  {runningPostHoc ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running Post-Hoc...
                    </>
                  ) : (
                    'Run Tukey HSD Post-Hoc Test'
                  )}
                </Button>
              </div>
            )}

            {/* Post-Hoc Results Display */}
            {postHocResults && (
              <div className="mt-4 space-y-4">
                <h5 className="font-semibold text-lg">Tukey HSD Post-Hoc Results</h5>
                <p className="text-sm text-slate-600">{postHocResults.description}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Group 1</th>
                        <th className="px-4 py-2 text-left">Group 2</th>
                        <th className="px-4 py-2 text-right">Mean Diff</th>
                        <th className="px-4 py-2 text-right">P-Value</th>
                        <th className="px-4 py-2 text-center">Significant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {postHocResults.pairwise_comparisons?.map((p, i) => (
                        <tr key={i} className={`border-b ${p.significant ? 'bg-green-50' : ''}`}>
                          <td className="px-4 py-2 font-medium">{p.group1}</td>
                          <td className="px-4 py-2 font-medium">{p.group2}</td>
                          <td className="px-4 py-2 text-right font-mono">{p.mean_diff}</td>
                          <td className="px-4 py-2 text-right font-mono">{p.p_value?.toFixed(4)}</td>
                          <td className="px-4 py-2 text-center">
                            {p.significant ? <CheckCircle className="w-5 h-5 text-green-600 inline" /> : <span className="text-slate-400">-</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {postHocResults.significant_pairs?.length > 0 && (
                  <div className="p-4 bg-green-50 rounded-xl text-sm text-green-800">
                    <strong>Significant Differences:</strong> {postHocResults.significant_pairs.map(p => `${p.group1} vs ${p.group2}`).join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'chi_square':
        const var1 = results.variable_1 || 'Variable 1';
        const var2 = results.variable_2 || 'Variable 2';
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Chi-Square Test of Independence</h4>
            <p className="text-slate-600">Testing association between <strong>{var1}</strong> and <strong>{var2}</strong></p>
            
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Chi-Square (χ²)</p>
                <p className="text-2xl font-mono font-bold">{results.chi_square?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className={`text-2xl font-mono font-bold ${results.p_value < 0.05 ? 'text-green-600' : 'text-slate-600'}`}>
                  {results.p_value?.toFixed(4)}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Degrees of Freedom</p>
                <p className="text-2xl font-mono font-bold">{results.degrees_of_freedom}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className="text-2xl">
                  {results.significant ? 
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto" /> : 
                    <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto" />
                  }
                </p>
              </div>
            </div>

            {/* Contingency Table */}
            {results.contingency_table && (
              <div>
                <h5 className="font-semibold mb-3">Contingency Table (Observed Frequencies)</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">{var1} \ {var2}</th>
                        {Object.keys(results.contingency_table).map(col => (
                          <th key={col} className="px-4 py-2 text-center">{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(Object.values(results.contingency_table)[0] || {}).map(row => (
                        <tr key={row} className="border-b">
                          <td className="px-4 py-2 font-medium">{row}</td>
                          {Object.entries(results.contingency_table).map(([col, values]) => (
                            <td key={col} className="px-4 py-2 text-center font-mono">{values[row]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800">
              <strong>Interpretation:</strong> {results.significant 
                ? `There is a statistically significant association between ${var1} and ${var2}, χ²(${results.degrees_of_freedom}) = ${results.chi_square?.toFixed(3)}, p = ${results.p_value?.toFixed(4)}. This suggests that the distribution of ${var2} differs significantly across categories of ${var1}.`
                : `There is no statistically significant association between ${var1} and ${var2} (χ²(${results.degrees_of_freedom}) = ${results.chi_square?.toFixed(3)}, p = ${results.p_value?.toFixed(4)} > 0.05). The variables appear to be independent.`}
            </div>
          </div>
        );

      case 'cronbach':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Cronbach's Alpha - Reliability Analysis</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Cronbach's α</p>
                <p className={`text-3xl font-mono font-bold ${
                  results.cronbach_alpha >= 0.7 ? 'text-green-600' : 
                  results.cronbach_alpha >= 0.6 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {results.cronbach_alpha?.toFixed(3)}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Reliability</p>
                <p className={`text-xl font-bold ${
                  results.reliability_level === 'Excellent' || results.reliability_level === 'Good' ? 'text-green-600' :
                  results.reliability_level === 'Acceptable' ? 'text-blue-600' :
                  results.reliability_level === 'Questionable' ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {results.reliability_level}
                </p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Items</p>
                <p className="text-2xl font-mono font-bold">{results.n_items}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Responses</p>
                <p className="text-2xl font-mono font-bold">{results.n_responses}</p>
              </div>
            </div>
            
            <div className="p-4 bg-indigo-50 rounded-xl text-sm text-indigo-800">
              <strong>Interpretation:</strong> {results.interpretation}
            </div>

            {/* Item Statistics */}
            {results.item_statistics && (
              <div>
                <h5 className="font-semibold mb-3">Item Statistics</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Item</th>
                        <th className="px-4 py-2 text-right">Mean</th>
                        <th className="px-4 py-2 text-right">Std Dev</th>
                        <th className="px-4 py-2 text-right">Item-Total Corr</th>
                        <th className="px-4 py-2 text-right">α if Deleted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(results.item_statistics.means || {}).map((item) => (
                        <tr key={item} className="border-b">
                          <td className="px-4 py-2 font-medium">{item}</td>
                          <td className="px-4 py-2 text-right font-mono">{results.item_statistics.means[item]?.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono">{results.item_statistics.std_devs[item]?.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-mono">{results.item_statistics.item_total_correlations[item]?.toFixed(3)}</td>
                          <td className={`px-4 py-2 text-right font-mono ${
                            results.item_statistics.alpha_if_item_deleted[item] > results.cronbach_alpha ? 'text-red-600' : ''
                          }`}>
                            {results.item_statistics.alpha_if_item_deleted[item]?.toFixed(3)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  * Red values in "α if Deleted" indicate items that may decrease reliability and could be considered for removal.
                </p>
              </div>
            )}

            {/* Reliability Guidelines */}
            {results.reliability_guidelines && (
              <div className="card-dashboard">
                <h5 className="font-semibold mb-3">Reliability Guidelines</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {Object.entries(results.reliability_guidelines).map(([range, level]) => (
                    <div key={range} className="flex justify-between p-2 bg-slate-50 rounded">
                      <span className="font-mono">{range}</span>
                      <span className={`font-medium ${
                        level === 'Excellent' || level === 'Good' ? 'text-green-600' :
                        level === 'Acceptable' ? 'text-blue-600' :
                        level === 'Questionable' ? 'text-yellow-600' : 'text-red-600'
                      }`}>{level}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'wordcloud':
      case 'thematic':
      case 'sentiment':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg capitalize">{analysis_type} Analysis</h4>
            
            {/* Enhanced Sentiment Display */}
            {results.sentiment_label && (
              <div className="space-y-4">
                {/* Main Sentiment Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Overall Sentiment</p>
                    <p className={`text-lg font-bold ${
                      results.sentiment_label.includes('Positive') ? 'text-green-600' :
                      results.sentiment_label.includes('Negative') ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {results.sentiment_label}
                    </p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Sentiment Score</p>
                    <p className={`text-xl font-mono font-bold ${
                      results.sentiment_score > 0 ? 'text-green-600' :
                      results.sentiment_score < 0 ? 'text-red-600' : 'text-slate-600'
                    }`}>
                      {results.sentiment_score > 0 ? '+' : ''}{results.sentiment_score?.toFixed(2)}
                    </p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Positive Words</p>
                    <p className="text-2xl font-mono font-bold text-green-600">{results.positive_count}</p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Negative Words</p>
                    <p className="text-2xl font-mono font-bold text-red-600">{results.negative_count}</p>
                  </div>
                </div>

                {/* Dominant Emotion */}
                {results.dominant_emotion && (
                  <div className="p-4 bg-indigo-50 rounded-xl text-center">
                    <p className="text-sm text-indigo-600">Dominant Emotion</p>
                    <p className="text-2xl font-bold text-indigo-700 capitalize">{results.dominant_emotion}</p>
                  </div>
                )}

                {/* Emotional Categories Breakdown */}
                {results.emotional_categories && (
                  <div className="space-y-4">
                    <h5 className="font-semibold text-lg">Emotional Categories</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(results.emotional_categories).map(([category, data]) => (
                        <div key={category} className={`p-3 rounded-lg ${
                          ['joy', 'satisfaction', 'trust', 'appreciation'].includes(category) 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-medium capitalize text-sm">{category}</span>
                            <span className={`font-mono font-bold ${
                              ['joy', 'satisfaction', 'trust', 'appreciation'].includes(category) 
                                ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {data.count}
                            </span>
                          </div>
                          {data.words_found && data.words_found.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {data.words_found.slice(0, 3).map((w, i) => (
                                <span key={i} className="text-xs bg-white px-1 rounded">{w}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                {results.total_emotional_words !== undefined && (
                  <div className="text-sm text-slate-600 text-center pt-2 border-t">
                    Analyzed {results.total_words_analyzed?.toLocaleString()} words • 
                    Found {results.total_emotional_words} emotional words
                  </div>
                )}
              </div>
            )}

            {results.themes && (
              <div className="space-y-4">
                <h5 className="font-semibold text-lg">Identified Themes</h5>
                {results.themes.map((theme, i) => (
                  <div key={i} className="card-dashboard border-l-4 border-indigo-500">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-lg">{theme.theme}</span>
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                        Frequency: {theme.frequency}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {theme.keywords_found?.map((kw, j) => (
                        <span key={j} className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-sm font-medium">
                          {kw}
                        </span>
                      ))}
                    </div>
                    {theme.example_sentences && theme.example_sentences.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm font-medium text-slate-600 mb-2">Example sentences:</p>
                        <ul className="space-y-2">
                          {theme.example_sentences.map((sent, k) => (
                            <li key={k} className="text-sm text-slate-600 pl-3 border-l-2 border-slate-200 italic">
                              {sent}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Enhanced Word Cloud Display - Bubble Style */}
            {results.word_data && (
              <div className="space-y-4">
                <h5 className="font-semibold text-lg">Word Cloud</h5>
                <div className="card-dashboard bg-gradient-to-br from-slate-50 to-indigo-50/30 p-8">
                  <div className="flex flex-wrap gap-3 justify-center items-center min-h-[280px]">
                    {results.word_data.slice(0, 40).map((item, i) => {
                      // Calculate bubble size based on frequency (min 48px, max 120px)
                      const maxFreq = results.word_data[0]?.frequency || 1;
                      const ratio = item.frequency / maxFreq;
                      const size = Math.max(48, Math.min(120, 48 + ratio * 72));
                      const fontSize = Math.max(10, Math.min(18, 10 + ratio * 10));
                      
                      // Color intensity based on frequency
                      const colors = [
                        'bg-indigo-600 text-white',
                        'bg-indigo-500 text-white',
                        'bg-indigo-400 text-white',
                        'bg-purple-500 text-white',
                        'bg-violet-500 text-white',
                        'bg-blue-500 text-white',
                        'bg-cyan-500 text-white',
                        'bg-teal-500 text-white',
                        'bg-emerald-500 text-white',
                        'bg-slate-500 text-white'
                      ];
                      const colorClass = ratio > 0.8 ? colors[0] :
                                        ratio > 0.6 ? colors[1] :
                                        ratio > 0.4 ? colors[2] :
                                        ratio > 0.3 ? colors[Math.floor(Math.random() * 3) + 3] :
                                        ratio > 0.2 ? colors[Math.floor(Math.random() * 3) + 6] :
                                        'bg-slate-400 text-white';
                      
                      return (
                        <div 
                          key={i}
                          className={`${colorClass} rounded-full flex items-center justify-center shadow-md hover:shadow-lg hover:scale-105 transition-all cursor-default`}
                          style={{ 
                            width: `${size}px`, 
                            height: `${size}px`,
                            fontSize: `${fontSize}px`
                          }}
                          title={`${item.word}: ${item.frequency} occurrences`}
                        >
                          <span className="font-medium text-center px-1 truncate max-w-full">
                            {item.word}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-500 text-center">Bubble size represents word frequency. Hover for details.</p>
                
                {/* Word Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Total Words</p>
                    <p className="text-xl font-mono font-bold text-slate-700">{results.total_words?.toLocaleString()}</p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Unique Words</p>
                    <p className="text-xl font-mono font-bold text-slate-700">{results.unique_words?.toLocaleString()}</p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Action Words</p>
                    <p className="text-xl font-mono font-bold text-blue-600">{results.action_words?.length || 0}</p>
                  </div>
                  <div className="card-dashboard text-center">
                    <p className="label-ui mb-1">Descriptive Words</p>
                    <p className="text-xl font-mono font-bold text-green-600">{results.descriptive_words?.length || 0}</p>
                  </div>
                </div>

                {/* Bigrams / Common Phrases */}
                {results.top_bigrams && results.top_bigrams.length > 0 && (
                  <div className="card-dashboard">
                    <h6 className="font-semibold mb-3">Common Phrases (Bigrams)</h6>
                    <div className="flex flex-wrap gap-2">
                      {results.top_bigrams.slice(0, 15).map((bg, i) => (
                        <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                          {bg.phrase} <span className="text-indigo-400">({bg.frequency})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action & Descriptive Words */}
                <div className="grid md:grid-cols-2 gap-4">
                  {results.action_words && results.action_words.length > 0 && (
                    <div className="card-dashboard">
                      <h6 className="font-semibold mb-2 text-blue-700">Action Words</h6>
                      <div className="flex flex-wrap gap-2">
                        {results.action_words.map((w, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm">{w}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {results.descriptive_words && results.descriptive_words.length > 0 && (
                    <div className="card-dashboard">
                      <h6 className="font-semibold mb-2 text-green-700">Descriptive Words</h6>
                      <div className="flex flex-wrap gap-2">
                        {results.descriptive_words.map((w, i) => (
                          <span key={i} className="px-2 py-1 bg-green-50 text-green-700 rounded text-sm">{w}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Legacy word_frequencies display (fallback) */}
            {results.word_frequencies && !results.word_data && (
              <div className="space-y-3">
                <h5 className="font-semibold">Word Frequencies</h5>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={Object.entries(results.word_frequencies).slice(0, 15).map(([word, freq]) => ({ word, freq }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="word" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="freq" fill="#4338ca" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {results.top_words && !results.themes && (
              <div className="flex flex-wrap gap-2 mt-4">
                {results.top_words.slice(0, 30).map((item, i) => (
                  <span 
                    key={i} 
                    className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full"
                    style={{ fontSize: `${Math.min(0.75 + (item.frequency / 50), 1.25)}rem` }}
                  >
                    {item.word}
                  </span>
                ))}
              </div>
            )}
          </div>
        );

      case 'keyword_context':
        // Keyword context search doesn't need to render in results - it's handled separately
        return null;

      case 'mann_whitney':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Mann-Whitney U Test Results</h4>
            <p className="text-sm text-slate-600">{results.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">U Statistic</p>
                <p className="text-2xl font-mono font-bold text-indigo-700">{results.u_statistic}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className="text-2xl font-mono font-bold">{results.p_value?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className={`text-2xl font-bold ${results.significant ? 'text-green-600' : 'text-slate-500'}`}>
                  {results.significant ? '✓ Yes' : '✗ No'}
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">{results.group1_name}</h5>
                <div className="text-sm space-y-1">
                  <p>Median: <span className="font-mono font-bold">{results.group1_median}</span></p>
                  <p>N: <span className="font-mono">{results.group1_n}</span></p>
                </div>
              </div>
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">{results.group2_name}</h5>
                <div className="text-sm space-y-1">
                  <p>Median: <span className="font-mono font-bold">{results.group2_median}</span></p>
                  <p>N: <span className="font-mono">{results.group2_n}</span></p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'kruskal_wallis':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Kruskal-Wallis H Test Results</h4>
            <p className="text-sm text-slate-600">{results.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">H Statistic</p>
                <p className="text-2xl font-mono font-bold text-indigo-700">{results.h_statistic}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className="text-2xl font-mono font-bold">{results.p_value?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className={`text-2xl font-bold ${results.significant ? 'text-green-600' : 'text-slate-500'}`}>
                  {results.significant ? '✓ Yes' : '✗ No'}
                </p>
              </div>
            </div>
            {results.group_statistics && (
              <div className="mt-4">
                <h5 className="font-semibold mb-2">Group Statistics (Median & IQR)</h5>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left">Group</th>
                        <th className="px-4 py-2 text-right">Median</th>
                        <th className="px-4 py-2 text-right">IQR</th>
                        <th className="px-4 py-2 text-right">N</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.group_statistics.map((g, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-4 py-2 font-medium">{g.group}</td>
                          <td className="px-4 py-2 text-right font-mono">{g.median}</td>
                          <td className="px-4 py-2 text-right font-mono">{g.iqr}</td>
                          <td className="px-4 py-2 text-right font-mono">{g.n}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'wilcoxon':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Wilcoxon Signed-Rank Test Results</h4>
            <p className="text-sm text-slate-600">{results.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">W Statistic</p>
                <p className="text-2xl font-mono font-bold text-indigo-700">{results.w_statistic}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">P-Value</p>
                <p className="text-2xl font-mono font-bold">{results.p_value?.toFixed(4)}</p>
              </div>
              <div className="card-dashboard text-center">
                <p className="label-ui mb-1">Significant</p>
                <p className={`text-2xl font-bold ${results.significant ? 'text-green-600' : 'text-slate-500'}`}>
                  {results.significant ? '✓ Yes' : '✗ No'}
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">{results.var1_name} (Pre)</h5>
                <p className="text-sm">Median: <span className="font-mono font-bold">{results.var1_median}</span></p>
              </div>
              <div className="card-dashboard">
                <h5 className="font-semibold mb-2">{results.var2_name} (Post)</h5>
                <p className="text-sm">Median: <span className="font-mono font-bold">{results.var2_median}</span></p>
              </div>
            </div>
            <p className="text-sm text-slate-600">Number of paired observations: <strong>{results.n_pairs}</strong></p>
          </div>
        );

      case 'posthoc_tukey':
        return (
          <div className="space-y-4">
            <h4 className="font-serif font-semibold text-lg">Tukey HSD Post-Hoc Test Results</h4>
            <p className="text-sm text-slate-600">{results.description}</p>
            {results.pairwise_comparisons && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Group 1</th>
                      <th className="px-4 py-2 text-left">Group 2</th>
                      <th className="px-4 py-2 text-right">Mean Diff</th>
                      <th className="px-4 py-2 text-right">P-Value</th>
                      <th className="px-4 py-2 text-center">Significant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.pairwise_comparisons.map((p, i) => (
                      <tr key={i} className={`border-b ${p.significant ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-2 font-medium">{p.group1}</td>
                        <td className="px-4 py-2 font-medium">{p.group2}</td>
                        <td className="px-4 py-2 text-right font-mono">{p.mean_diff}</td>
                        <td className="px-4 py-2 text-right font-mono">{p.p_value?.toFixed(4)}</td>
                        <td className="px-4 py-2 text-center">
                          {p.significant ? <CheckCircle className="w-5 h-5 text-green-600 inline" /> : <span className="text-slate-400">-</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {results.significant_pairs && results.significant_pairs.length > 0 && (
              <div className="p-4 bg-green-50 rounded-xl text-sm text-green-800">
                <strong>Significant Differences Found:</strong> {results.significant_pairs.map(p => `${p.group1} vs ${p.group2}`).join(', ')}
              </div>
            )}
          </div>
        );

      default:
        return <pre className="bg-slate-100 p-4 rounded overflow-auto text-sm">{JSON.stringify(results, null, 2)}</pre>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700"></div>
      </div>
    );
  }

  const hasQuantData = datasets.some(d => d.data_type === 'quantitative');
  const hasQualData = datasets.some(d => d.data_type === 'qualitative');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-slate-300 p-6 hidden lg:block">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-serif font-bold text-white">StatsXY</span>
        </div>

        <nav className="space-y-2">
          {/* Dashboard at top */}
          <Link 
            to="/dashboard" 
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          
          <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mt-4 mb-2">Research Tools</p>
          <Link 
            to={`/project/${projectId}/methodology`}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <FlaskConical className="w-5 h-5" />
            Research Design
          </Link>
          <Link 
            to={`/project/${projectId}/literature-review`}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            Literature Review
          </Link>
          <Link 
            to={`/project/${projectId}/proposal`}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            data-testid="sidebar-research-proposal"
          >
            <FileText className="w-5 h-5" />
            Research Proposal
          </Link>
          <Link 
            to={`/project/${projectId}/sample-size`}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <Calculator className="w-5 h-5" />
            Sample Size
          </Link>
          
          <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mt-6 mb-2">Data Collection</p>
          <Link 
            to={`/project/${projectId}/data-collection`}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <ClipboardList className="w-5 h-5" />
            Collect Data
          </Link>
          
          <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mt-6 mb-2">Data Analysis</p>
          <button 
            onClick={() => setActiveTab('data')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <BarChart3 className="w-5 h-5" />
            Analyze Data
          </button>
          {youtubeChannelUrl && (
            <a 
              href={youtubeChannelUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <Video className="w-5 h-5" />
              Tutorials
            </a>
          )}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <button 
            onClick={logout}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors w-full px-4 py-2"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-indigo-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex-1">
              <h1 className="text-xl font-serif font-bold text-slate-900 line-clamp-1">{project?.title}</h1>
              <p className="text-sm text-slate-500">
                {project?.research_type} research • Created {new Date(project?.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-10">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-white border border-slate-200 p-1">
              <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                Overview
              </TabsTrigger>
              <TabsTrigger value="data" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                Data
              </TabsTrigger>
              <TabsTrigger value="analysis" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                Analysis
              </TabsTrigger>
              <TabsTrigger value="results" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
                Results
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="card-dashboard" data-testid="project-overview">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-serif font-semibold text-slate-900">Research Details</h3>
                    {!isEditingDetails ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setIsEditingDetails(true)}
                        className="text-indigo-600 hover:text-indigo-700"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setIsEditingDetails(false);
                            setEditedTitle(project?.title || '');
                            setEditedAim(project?.research_aim || '');
                            setEditedObjectives(project?.research_objectives || '');
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={handleSaveDetails}
                          disabled={savingDetails}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {savingDetails ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Save
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {isEditingDetails ? (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-xs text-slate-500 uppercase">Title</Label>
                        {(project?.title_edit_count || 0) >= 3 ? (
                          <p className="text-sm text-red-500 mt-1">Title edit limit reached (3/3). You can no longer change the title.</p>
                        ) : (
                          <>
                            <Input
                              value={editedTitle}
                              onChange={(e) => setEditedTitle(e.target.value)}
                              className="mt-1"
                              placeholder="Research title"
                            />
                            <p className="text-xs text-amber-600 mt-1">
                              Title edits remaining: {3 - (project?.title_edit_count || 0)} of 3
                            </p>
                          </>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 uppercase">Research Aim</Label>
                        <Textarea
                          value={editedAim}
                          onChange={(e) => setEditedAim(e.target.value)}
                          className="mt-1"
                          rows={3}
                          placeholder="The aim of this research is to..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500 uppercase">Research Objectives</Label>
                        <Textarea
                          value={editedObjectives}
                          onChange={(e) => setEditedObjectives(e.target.value)}
                          className="mt-1"
                          rows={4}
                          placeholder="1. To assess...&#10;2. To identify...&#10;3. To examine..."
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <p className="label-ui mb-1">Title</p>
                        <p className="text-slate-900 font-medium">{project?.title}</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="label-ui">Research Aim</p>
                          {!project?.research_aim && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleGenerateAim}
                              disabled={generatingAim}
                              className="text-indigo-600 hover:text-indigo-700 h-6 px-2 text-xs"
                            >
                              {generatingAim ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                              ) : (
                                <><Sparkles className="w-3 h-3 mr-1" /> Generate with AI</>
                              )}
                            </Button>
                          )}
                        </div>
                        <p className="text-slate-700">{project?.research_aim || <span className="text-slate-400 italic">Not specified</span>}</p>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="label-ui">Research Objectives</p>
                          {!project?.research_objectives && project?.research_aim && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleGenerateObjectives}
                              disabled={generatingObjectives}
                              className="text-indigo-600 hover:text-indigo-700 h-6 px-2 text-xs"
                            >
                              {generatingObjectives ? (
                                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                              ) : (
                                <><Sparkles className="w-3 h-3 mr-1" /> Generate with AI</>
                              )}
                            </Button>
                          )}
                        </div>
                        <p className="text-slate-700 whitespace-pre-line">{project?.research_objectives || <span className="text-slate-400 italic">Not specified</span>}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="card-dashboard">
                  <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Project Status</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Research Type</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        project?.research_type === 'quantitative' ? 'bg-blue-100 text-blue-700' :
                        project?.research_type === 'qualitative' ? 'bg-purple-100 text-purple-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {project?.research_type}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Datasets</span>
                      <span className="font-mono font-bold">{datasets.length}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="text-slate-600">Analyses</span>
                      <span className="font-mono font-bold">{analyses.length}</span>
                    </div>
                    {project?.research_type === 'mixed' ? (
                      <>
                        {project?.quant_sample_size && (
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-600">Quant Sample</span>
                            <span className="font-mono">{project.quant_sample_size}</span>
                          </div>
                        )}
                        {project?.qual_sample_size && (
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-slate-600">Qual Participants</span>
                            <span className="font-mono">{project.qual_sample_size}</span>
                          </div>
                        )}
                      </>
                    ) : project?.sample_size && (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Sample Size</span>
                        <span className="font-mono">{project.sample_size}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Data Tab */}
            <TabsContent value="data" className="space-y-6">
              <div className="card-dashboard" data-testid="data-upload-section">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Upload Data</h3>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">
                    Upload CSV or Excel for quantitative data, TXT for qualitative data
                  </p>
                  <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Uploading...' : 'Select File'}
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,.txt"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                      data-testid="file-upload-input"
                    />
                  </label>
                </div>
              </div>

              {/* Sample Data Downloads */}
              <div className="card-dashboard">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Sample Data Templates</h3>
                <p className="text-slate-600 mb-4">Download sample data files to see the expected format for each analysis type:</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button 
                    onClick={() => handleDownloadSample('quantitative')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-quant"
                  >
                    <FileDown className="w-6 h-6 text-blue-600 mb-2" />
                    <h4 className="font-medium text-sm">Descriptive / Regression</h4>
                    <p className="text-xs text-slate-500">Numeric variables CSV</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('likert')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-likert"
                  >
                    <FileDown className="w-6 h-6 text-purple-600 mb-2" />
                    <h4 className="font-medium text-sm">Likert Survey</h4>
                    <p className="text-xs text-slate-500">5-point scale responses</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('ttest')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-ttest"
                  >
                    <FileDown className="w-6 h-6 text-green-600 mb-2" />
                    <h4 className="font-medium text-sm">T-Test Data</h4>
                    <p className="text-xs text-slate-500">Two-group comparison</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('anova')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-anova"
                  >
                    <FileDown className="w-6 h-6 text-orange-600 mb-2" />
                    <h4 className="font-medium text-sm">ANOVA Data</h4>
                    <p className="text-xs text-slate-500">Multi-group comparison</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('chisquare')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-chisquare"
                  >
                    <FileDown className="w-6 h-6 text-pink-600 mb-2" />
                    <h4 className="font-medium text-sm">Chi-Square Data</h4>
                    <p className="text-xs text-slate-500">Categorical variables</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('cronbach')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-cronbach"
                  >
                    <FileDown className="w-6 h-6 text-indigo-600 mb-2" />
                    <h4 className="font-medium text-sm">Cronbach&apos;s Alpha</h4>
                    <p className="text-xs text-slate-500">Scale reliability items</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('mann_whitney')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left"
                    data-testid="download-sample-mannwhitney"
                  >
                    <FileDown className="w-6 h-6 text-teal-600 mb-2" />
                    <h4 className="font-medium text-sm">Mann-Whitney U</h4>
                    <p className="text-xs text-slate-500">Non-parametric (2 groups)</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('kruskal_wallis')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left"
                    data-testid="download-sample-kruskal"
                  >
                    <FileDown className="w-6 h-6 text-teal-600 mb-2" />
                    <h4 className="font-medium text-sm">Kruskal-Wallis H</h4>
                    <p className="text-xs text-slate-500">Non-parametric (3+ groups)</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('wilcoxon')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all text-left"
                    data-testid="download-sample-wilcoxon"
                  >
                    <FileDown className="w-6 h-6 text-teal-600 mb-2" />
                    <h4 className="font-medium text-sm">Wilcoxon (Paired)</h4>
                    <p className="text-xs text-slate-500">Pre/Post comparisons</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('ztest')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-cyan-300 hover:bg-cyan-50 transition-all text-left"
                    data-testid="download-sample-ztest"
                  >
                    <FileDown className="w-6 h-6 text-cyan-600 mb-2" />
                    <h4 className="font-medium text-sm">Z-Test Data</h4>
                    <p className="text-xs text-slate-500">Large sample (n≥30)</p>
                  </button>
                  <button 
                    onClick={() => handleDownloadSample('qualitative')}
                    className="p-4 border border-slate-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all text-left"
                    data-testid="download-sample-qual"
                  >
                    <FileDown className="w-6 h-6 text-rose-600 mb-2" />
                    <h4 className="font-medium text-sm">Qualitative Data</h4>
                    <p className="text-xs text-slate-500">Interview transcripts TXT</p>
                  </button>
                </div>
              </div>

              {/* Uploaded Datasets with Download */}
              {datasets.length > 0 && (
                <div className="card-dashboard">
                  <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Uploaded Datasets</h3>
                  <div className="space-y-3">
                    {datasets.map((dataset) => (
                      <div key={dataset.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg group">
                        <div className="flex items-center gap-3">
                          {dataset.data_type === 'quantitative' ? 
                            <BarChart3 className="w-5 h-5 text-blue-600" /> :
                            <FileText className="w-5 h-5 text-purple-600" />
                          }
                          <div>
                            <p className="font-medium">{dataset.filename}</p>
                            <p className="text-sm text-slate-500">
                              {dataset.data_type} • {dataset.validation?.rows || 'N/A'} rows
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            dataset.data_type === 'quantitative' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {dataset.data_type}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDownloadDataset(dataset.id, dataset.filename)}
                            data-testid={`download-dataset-${dataset.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleDeleteDataset(dataset.id, dataset.filename)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-dataset-${dataset.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Analysis Tab */}
            <TabsContent value="analysis" className="space-y-6">
              {datasets.length === 0 ? (
                <div className="card-dashboard text-center py-12">
                  <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600">Please upload data first before running analyses.</p>
                  <Button onClick={() => setActiveTab('data')} className="btn-secondary mt-4">
                    Go to Data Tab
                  </Button>
                </div>
              ) : (
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* Analysis Options */}
                  <div className="card-dashboard lg:col-span-1" data-testid="analysis-options">
                    <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Analysis Options</h3>
                    
                    <div className="space-y-4">
                      {/* Dataset Selection */}
                      {datasets.length > 1 && (
                        <div>
                          <Label>Select Dataset</Label>
                          <Select value={selectedDatasetId || "auto"} onValueChange={handleDatasetChange}>
                            <SelectTrigger className="input-field mt-1" data-testid="dataset-select">
                              <SelectValue placeholder="Use first available" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="auto">Use first available</SelectItem>
                              {datasets.map((ds) => (
                                <SelectItem key={ds.id} value={ds.id}>
                                  {ds.filename} ({ds.data_type})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <Label>Analysis Type</Label>
                        <Select value={analysisType} onValueChange={setAnalysisType}>
                          <SelectTrigger className="input-field mt-1" data-testid="analysis-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {hasQuantData && (
                              <>
                                <SelectItem value="descriptive">Descriptive Statistics</SelectItem>
                                <SelectItem value="correlation">Correlation Analysis</SelectItem>
                                <SelectItem value="regression">Regression Analysis</SelectItem>
                                <SelectItem value="cronbach">Cronbach&apos;s Alpha (Reliability)</SelectItem>
                                <SelectItem value="ttest">T-Test</SelectItem>
                                <SelectItem value="ztest">Z-Test (Large Samples)</SelectItem>
                                <SelectItem value="anova">ANOVA</SelectItem>
                                <SelectItem value="chi_square">Chi-Square Test</SelectItem>
                                <SelectItem value="mann_whitney">Mann-Whitney U (Non-Parametric)</SelectItem>
                                <SelectItem value="kruskal_wallis">Kruskal-Wallis H (Non-Parametric)</SelectItem>
                                <SelectItem value="wilcoxon">Wilcoxon Signed-Rank (Paired)</SelectItem>
                              </>
                            )}
                            {hasQualData && (
                              <>
                                <SelectItem value="thematic">Thematic Analysis</SelectItem>
                                <SelectItem value="sentiment">Sentiment Analysis</SelectItem>
                                <SelectItem value="wordcloud">Word Cloud</SelectItem>
                                <SelectItem value="keyword_context">Keyword Context Search</SelectItem>
                                <SelectItem value="critical_analysis">Critical Analysis (AI)</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Test Explanation Section */}
                      <TestExplanation analysisType={analysisType} />

                      {/* Keyword Context Search Input */}
                      {analysisType === 'keyword_context' && (
                        <div className="space-y-3">
                          <div>
                            <Label>Enter Keywords (comma-separated)</Label>
                            <Input
                              value={keywordSearch}
                              onChange={(e) => setKeywordSearch(e.target.value)}
                              placeholder="e.g., experience, challenge, outcome"
                              className="input-field mt-1"
                              data-testid="keyword-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              Enter one or more keywords to find sentences containing them
                            </p>
                          </div>
                          <Button
                            onClick={handleKeywordSearch}
                            disabled={searchingKeywords || !keywordSearch.trim()}
                            className="btn-primary w-full"
                            data-testid="search-keywords-btn"
                          >
                            {searchingKeywords ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Searching...
                              </>
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-2" />
                                Search Keywords
                              </>
                            )}
                          </Button>
                          
                          {/* Keyword Results */}
                          {keywordResults && (
                            <div className="mt-4 space-y-4">
                              <div className="text-sm text-slate-600">
                                Found in {keywordResults.total_sentences} total sentences
                              </div>
                              {Object.entries(keywordResults.results).map(([keyword, data]) => (
                                <div key={keyword} className="card-dashboard border-l-4 border-indigo-500">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="font-semibold text-lg capitalize">{keyword}</span>
                                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                                      {data.count} occurrence{data.count !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  {data.sentences.length > 0 ? (
                                    <ul className="space-y-2">
                                      {data.sentences.map((sent, i) => (
                                        <li key={i} className="text-sm text-slate-600 pl-3 border-l-2 border-slate-200">
                                          <span dangerouslySetInnerHTML={{ 
                                            __html: sent.highlighted.replace(/\*\*(.*?)\*\*/g, '<strong class="text-indigo-700 bg-indigo-50 px-1 rounded">$1</strong>') 
                                          }} />
                                        </li>
                                      ))}
                                    </ul>
                                  ) : (
                                    <p className="text-sm text-slate-400 italic">No sentences found containing this keyword</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Correlation Method Selection */}
                      {analysisType === 'correlation' && (
                        <div>
                          <Label>Correlation Method</Label>
                          <Select value={correlationMethod} onValueChange={setCorrelationMethod}>
                            <SelectTrigger className="input-field mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pearson">Pearson (for continuous data)</SelectItem>
                              <SelectItem value="spearman">Spearman (for ordinal/ranked data)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Variable Selection */}
                      {hasQuantData && ['descriptive', 'correlation', 'regression', 'chi_square', 'cronbach'].includes(analysisType) && (
                        <div>
                          <Label className="mb-2 block">Select Variables</Label>
                          <div className="max-h-48 overflow-y-auto space-y-2 p-3 bg-slate-50 rounded-lg">
                            {columns.map((col) => (
                              <label key={col} className="flex items-center gap-2 cursor-pointer">
                                <Checkbox
                                  checked={selectedVariables.includes(col)}
                                  onCheckedChange={() => toggleVariable(col)}
                                />
                                <span className="text-sm">{col}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Regression Options */}
                      {analysisType === 'regression' && (
                        <div>
                          <Label>Dependent Variable</Label>
                          <Select value={dependentVar} onValueChange={setDependentVar}>
                            <SelectTrigger className="input-field mt-1">
                              <SelectValue placeholder="Select dependent variable" />
                            </SelectTrigger>
                            <SelectContent>
                              {selectedVariables.map((v) => (
                                <SelectItem key={v} value={v}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* T-Test / ANOVA / Non-Parametric Options */}
                      {['ttest', 'anova', 'mann_whitney', 'kruskal_wallis'].includes(analysisType) && (
                        <>
                          <div>
                            <Label>Test Variable</Label>
                            <Select 
                              value={selectedVariables[0] || ''} 
                              onValueChange={(v) => setSelectedVariables([v])}
                            >
                              <SelectTrigger className="input-field mt-1">
                                <SelectValue placeholder="Select variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Group Variable</Label>
                            <Select value={groupVar} onValueChange={setGroupVar}>
                              <SelectTrigger className="input-field mt-1">
                                <SelectValue placeholder="Select grouping variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.filter(c => c !== selectedVariables[0]).map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {analysisType === 'mann_whitney' && (
                            <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                              Mann-Whitney U is a non-parametric test for comparing two independent groups when data is not normally distributed.
                            </p>
                          )}
                          {analysisType === 'kruskal_wallis' && (
                            <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                              Kruskal-Wallis H is a non-parametric alternative to one-way ANOVA for 3+ groups.
                            </p>
                          )}
                        </>
                      )}

                      {/* Wilcoxon (Paired) Options */}
                      {analysisType === 'wilcoxon' && (
                        <div className="space-y-3">
                          <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                            Wilcoxon Signed-Rank test compares two related/paired measurements (e.g., pre and post-treatment).
                          </p>
                          <div>
                            <Label>First Variable (e.g., Pre-test)</Label>
                            <Select 
                              value={selectedVariables[0] || ''} 
                              onValueChange={(v) => setSelectedVariables([v, selectedVariables[1] || ''])}
                            >
                              <SelectTrigger className="input-field mt-1">
                                <SelectValue placeholder="Select first variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Second Variable (e.g., Post-test)</Label>
                            <Select 
                              value={selectedVariables[1] || ''} 
                              onValueChange={(v) => setSelectedVariables([selectedVariables[0] || '', v])}
                            >
                              <SelectTrigger className="input-field mt-1">
                                <SelectValue placeholder="Select second variable" />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.filter(c => c !== selectedVariables[0]).map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {/* Z-Test Options */}
                      {analysisType === 'ztest' && (
                        <div className="space-y-3">
                          <div>
                            <Label>Test Variable</Label>
                            <Select 
                              value={selectedVariables[0] || ''} 
                              onValueChange={(v) => setSelectedVariables([v])}
                            >
                              <SelectTrigger className="input-field mt-1">
                                <SelectValue placeholder="Select variable to test" />
                              </SelectTrigger>
                              <SelectContent>
                                {columns.map((v) => (
                                  <SelectItem key={v} value={v}>{v}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Population Mean (μ₀) *</Label>
                            <Input 
                              type="number"
                              value={populationMean}
                              onChange={(e) => setPopulationMean(e.target.value)}
                              placeholder="Enter known population mean"
                              className="input-field mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">The hypothesized population mean to compare against</p>
                          </div>
                          <div>
                            <Label>Population Std Dev (σ) - Optional</Label>
                            <Input 
                              type="number"
                              value={populationStd}
                              onChange={(e) => setPopulationStd(e.target.value)}
                              placeholder="Leave blank to use sample std"
                              className="input-field mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">If unknown, sample standard deviation will be used</p>
                          </div>
                          <button
                            onClick={() => handleDownloadSample('ztest')}
                            className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            <FileDown className="w-3 h-3" />
                            Download Z-test sample data
                          </button>
                        </div>
                      )}

                      {/* Critical Analysis Button (Qualitative) */}
                      {analysisType === 'critical_analysis' && (
                        <div className="space-y-3">
                          <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            This AI-powered analysis will generate a scholarly critical discussion of your interview data, analyzing participant perspectives, areas of agreement and disagreement.
                          </p>
                          <Button 
                            onClick={handleRunCriticalAnalysis} 
                            className="btn-primary w-full"
                            disabled={runningCritical}
                          >
                            {runningCritical ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating Critical Analysis...
                              </>
                            ) : (
                              <>
                                <Brain className="w-4 h-4 mr-2" />
                                Run Critical Analysis
                              </>
                            )}
                          </Button>
                        </div>
                      )}

                      {analysisType !== 'critical_analysis' && (
                        <Button 
                          onClick={handleRunAnalysis} 
                          className="btn-primary w-full"
                          disabled={analyzing || analysisType === 'keyword_context'}
                          data-testid="run-analysis-btn"
                        >
                          {analyzing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Analyzing...
                            </>
                          ) : (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Run Analysis
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Results Panel */}
                  <div className="card-dashboard lg:col-span-2" data-testid="analysis-results">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-serif font-semibold text-slate-900">Results</h3>
                      {currentResults && (
                        <Button 
                          onClick={handleGetInterpretation}
                          className="btn-ghost"
                          disabled={interpreting}
                          data-testid="interpret-btn"
                        >
                          {interpreting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Brain className="w-4 h-4 mr-2" />
                          )}
                          AI Interpretation
                        </Button>
                      )}
                    </div>

                    {/* Critical Analysis Results */}
                    {criticalAnalysis && (
                      <div className="mb-6 p-6 bg-amber-50 rounded-xl border border-amber-200">
                        <div className="flex items-center gap-2 mb-4">
                          <Brain className="w-5 h-5 text-amber-700" />
                          <h4 className="font-serif font-semibold text-amber-900">Critical Analysis of Interview Data</h4>
                        </div>
                        <p className="text-xs text-amber-600 mb-4">Dataset: {criticalAnalysis.dataset_name}</p>
                        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap bg-white p-4 rounded-lg border">
                          {criticalAnalysis.critical_analysis}
                        </div>
                      </div>
                    )}

                    {currentResults ? (
                      <div className="space-y-6">
                        {renderResults()}
                        
                        {interpretation && (
                          <div className="mt-6 p-6 bg-indigo-50 rounded-xl border border-indigo-100">
                            <div className="flex items-center gap-2 mb-4">
                              <Brain className="w-5 h-5 text-indigo-600" />
                              <h4 className="font-serif font-semibold text-indigo-900">AI Interpretation</h4>
                            </div>
                            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
                              {interpretation}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : !criticalAnalysis ? (
                      <div className="text-center py-12 text-slate-500">
                        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Select analysis options and click "Run Analysis" to see results</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Results History Tab */}
            <TabsContent value="results" className="space-y-6">
              {supervisorAnalysisComments.length > 0 && (
                <div className="card-dashboard border-red-300 bg-red-50/40" data-testid="supervisor-analysis-comments">
                  <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-red-600" />
                    Supervisor&apos;s Comments on Your Analysis Results
                  </h3>
                  <div className="space-y-2">
                    {supervisorAnalysisComments.map((c) => (
                      <div key={c.id} className="border-l-4 border-red-500 bg-red-50 pl-3 pr-3 py-2 rounded text-sm">
                        <div className="text-xs text-red-800 font-semibold mb-0.5">
                          <MessageSquare className="inline w-3 h-3 mr-1" />
                          {c.author_name || c.author_email} · {new Date(c.created_at).toLocaleString()}
                        </div>
                        <p className="text-red-700 font-medium whitespace-pre-wrap">{c.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="card-dashboard" data-testid="results-history">
                <h3 className="text-lg font-serif font-semibold text-slate-900 mb-4">Saved Analysis Results</h3>
                <p className="text-sm text-slate-500 mb-4">Your analysis results are automatically saved. Re-running an analysis will overwrite the previous result.</p>
                
                {analyses.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No analyses run yet. Go to the Analysis tab to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {analyses.map((analysis, i) => (
                      <div key={analysis.id || i} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button 
                          onClick={() => setSelectedSavedAnalysis(selectedSavedAnalysis === analysis.id ? null : analysis.id)}
                          className="w-full p-4 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${analysis.interpretation ? 'bg-green-100' : 'bg-slate-200'}`}>
                                {analysis.interpretation ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                  <BarChart3 className="w-4 h-4 text-slate-500" />
                                )}
                              </div>
                              <div>
                                <span className="font-medium capitalize">{analysis.analysis_type?.replace(/_/g, ' ')}</span>
                                {analysis.interpretation && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    With AI Interpretation
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-slate-500">
                              {new Date(analysis.created_at).toLocaleString()}
                            </span>
                          </div>
                        </button>
                        
                        {selectedSavedAnalysis === analysis.id && (
                          <div className="p-4 border-t border-slate-200">
                            <p className="text-sm text-slate-600 mb-3">
                              <strong>Variables:</strong> {analysis.variables?.join(', ') || 'All available'}
                            </p>
                            
                            {/* Show key results summary */}
                            {analysis.results && (
                              <div className="bg-slate-50 rounded-lg p-4 mb-3">
                                <p className="text-xs text-slate-500 uppercase mb-2">Results Summary</p>
                                <pre className="text-xs text-slate-700 overflow-auto max-h-40">
                                  {JSON.stringify(analysis.results, null, 2).slice(0, 500)}
                                  {JSON.stringify(analysis.results).length > 500 && '...'}
                                </pre>
                              </div>
                            )}
                            
                            {/* Show saved interpretation */}
                            {analysis.interpretation && (
                              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-2">
                                  <Brain className="w-4 h-4 text-indigo-600" />
                                  <p className="text-sm font-medium text-indigo-900">AI Interpretation</p>
                                </div>
                                <p className="text-sm text-slate-700 whitespace-pre-line">{analysis.interpretation}</p>
                                {analysis.interpretation_saved_at && (
                                  <p className="text-xs text-slate-400 mt-2">
                                    Saved: {new Date(analysis.interpretation_saved_at).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3"
                              onClick={() => {
                                setCurrentResults(analysis);
                                setInterpretation(analysis.interpretation || '');
                                setActiveTab('analysis');
                              }}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              View Full Results
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Overwrite Confirmation Dialog */}
      <Dialog open={showOverwriteDialog} onOpenChange={setShowOverwriteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Overwrite Existing Analysis?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            You already have a saved <strong className="capitalize">{pendingAnalysis?.analysis_type?.replace(/_/g, ' ')}</strong> analysis for this project. 
            Running a new analysis will overwrite the previous results and any saved interpretation.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowOverwriteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmOverwrite} className="bg-amber-600 hover:bg-amber-700">
              Yes, Overwrite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
